import type { AlbumDocument, AlbumPage, Placement } from "../lib/types.ts";
import { pointInPlacement, snapValue } from "../lib/geometry.ts";
import * as db from "../storage/indexeddb.ts";
import { importPhotoFile } from "../storage/assets.ts";
import { router } from "../router.ts";
import { store } from "../store.ts";
import { takePendingImportFile } from "../pending-import.ts";
import { renderPage, spreadIndices } from "./page-render.ts";

type Handle = "move" | "resize-se" | "rotate";

function ghostButton(
  action: string,
  label: string,
  icon: string,
  opts: { danger?: boolean; disabled?: boolean } = {},
): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = `ghost-icon${opts.danger ? " danger" : ""}`;
  btn.dataset.action = action;
  btn.setAttribute("aria-label", label);
  btn.title = label;
  btn.textContent = icon;
  if (opts.disabled) btn.disabled = true;
  return btn;
}

function pageIndexOfPlacement(album: AlbumDocument, placementId: string | null): number | null {
  if (!placementId) return null;
  for (let i = 0; i < album.pages.length; i++) {
    if (album.pages[i]!.placements.some((p) => p.id === placementId)) return i;
  }
  return null;
}

function applyPlacementStyle(el: HTMLElement, placement: Placement): void {
  el.style.left = `${placement.x * 100}%`;
  el.style.top = `${placement.y * 100}%`;
  el.style.width = `${placement.width * 100}%`;
  el.style.height = `${placement.height * 100}%`;
  el.style.transform = `rotate(${placement.rotation}rad)`;
}

export async function renderEditor(
  root: HTMLElement,
  albumId: string,
  pageIndex: number,
  signal: AbortSignal,
): Promise<() => void> {
  const album = await db.getAlbum(albumId);
  if (signal.aborted) return () => {};
  if (!album) {
    const err = document.createElement("p");
    err.className = "status error";
    err.textContent = "Album not found.";
    root.replaceChildren(err);
    return () => {};
  }

  let currentPage = Math.max(0, Math.min(pageIndex, album.pages.length - 1));
  let selectedId: string | null = null;
  let activePointer: {
    placementId: string;
    pageId: string;
    pageIndex: number;
    handle: Handle;
    startX: number;
    startY: number;
    snapshot: Placement;
    albumSnapshot: AlbumDocument;
    pageEl: HTMLElement;
    photoEl: HTMLElement;
  } | null = null;
  let previewPatch: Partial<Placement> | null = null;
  let paintGeneration = 0;
  let importError: string | null = null;
  let pendingImportHandled = false;

  function aborted(): boolean {
    return signal.aborted;
  }

  function targetPageIndex(fresh: AlbumDocument): number {
    return pageIndexOfPlacement(fresh, selectedId) ?? currentPage;
  }

  async function paint(): Promise<void> {
    if (aborted()) return;
    const generation = ++paintGeneration;
    const fresh = await db.getAlbum(albumId);
    if (!fresh || generation !== paintGeneration || aborted()) return;
    const paintAlbum = fresh;

    const view = document.createElement("div");
    view.className = "overlay-view editor";

    const chrome = document.createElement("div");
    chrome.className = "overlay-chrome";

    const backBtn = ghostButton("back", "Back", "←");
    backBtn.classList.add("overlay-chrome-back");

    const titleInput = document.createElement("input");
    titleInput.type = "text";
    titleInput.className = "overlay-title-input";
    titleInput.value = fresh.title;
    titleInput.setAttribute("aria-label", "Album title");

    const actions = document.createElement("div");
    actions.className = "overlay-chrome-actions";
    actions.append(
      ghostButton("undo", "Undo", "↶"),
      ghostButton("redo", "Redo", "↷"),
      ghostButton("view", "View", "◎"),
      ghostButton("delete-album", "Delete Album", "⌫", { danger: true }),
    );
    chrome.append(backBtn, titleInput, actions);

    if (importError) {
      const err = document.createElement("p");
      err.className = "status error";
      err.textContent = importError;
      view.appendChild(err);
    }

    const { left: leftIndex, right: rightIndex } = spreadIndices(currentPage);
    const focusPageIndex = targetPageIndex(fresh);
    const focusPage = fresh.pages[focusPageIndex] ?? null;

    const displayAlbum =
      previewPatch && selectedId && focusPage
        ? applyPreview(fresh, focusPage.id, selectedId, previewPatch)
        : fresh;

    const spread = document.createElement("div");
    spread.className = "album-spread editor";
    spread.setAttribute("role", "application");
    spread.setAttribute("aria-label", "Album page editor");

    async function mountPage(index: number, side: "left" | "right"): Promise<HTMLElement> {
      const canvas = document.createElement("div");
      canvas.className = `album-page ${side} editor-canvas`;
      canvas.dataset.pageIndex = String(index);
      await renderPage(canvas, displayAlbum, index, true, selectedId);
      return canvas;
    }

    const leftCanvas = await mountPage(leftIndex, "left");
    const rightCanvas = await mountPage(rightIndex, "right");
    spread.append(leftCanvas, rightCanvas);
    if (generation !== paintGeneration || aborted()) return;

    const pageNav = document.createElement("div");
    pageNav.className = "page-nav";
    fresh.pages.forEach((_p: AlbumPage, i: number) => {
      const btn = ghostButton("page", `Page ${i + 1}`, String(i + 1));
      btn.classList.add("page-tab");
      if (i === currentPage) btn.classList.add("active");
      btn.draggable = true;
      btn.addEventListener("click", () => {
        currentPage = i;
        selectedId = null;
        previewPatch = null;
        router.navigate({ name: "editor", albumId, page: currentPage }, true, true);
        view.inert = true;
        view.setAttribute("aria-busy", "true");
        void paint().finally(() => {
          if (!view.isConnected) return;
          view.inert = false;
          view.removeAttribute("aria-busy");
        });
      });
      btn.addEventListener("dragstart", (e) => {
        e.dataTransfer?.setData("text/plain", String(i));
      });
      btn.addEventListener("dragover", (e) => e.preventDefault());
      btn.addEventListener("drop", (e) => {
        e.preventDefault();
        const from = Number(e.dataTransfer?.getData("text/plain"));
        if (!Number.isFinite(from) || from === i) return;
        void store.reorderPage(albumId, fresh.pages[from]!.id, i).then(() => paint());
      });
      pageNav.appendChild(btn);
    });

    const tools = document.createElement("div");
    tools.className = "spread-tools";

    const importLabel = document.createElement("label");
    importLabel.className = "ghost-icon ghost-icon-label file-btn";
    importLabel.title = "Import Photo";
    importLabel.setAttribute("aria-label", "Import Photo");
    importLabel.textContent = "＋";
    const importInput = document.createElement("input");
    importInput.type = "file";
    importInput.accept = "image/*";
    importInput.hidden = true;
    importInput.dataset.action = "import";
    importLabel.appendChild(importInput);

    tools.append(
      importLabel,
      ghostButton("add-page", "Add Page", "⊞"),
      ghostButton("remove-page", "Remove Page", "⊟"),
    );

    if (selectedId && focusPage) {
      const selectionTools = document.createElement("div");
      selectionTools.className = "selection-tools";
      selectionTools.append(
        ghostButton("move-up", "Layer Up", "↑"),
        ghostButton("move-down", "Layer Down", "↓"),
        ghostButton("set-cover", "Set Cover", "★"),
        ghostButton("delete-photo", "Delete Photo", "✕", { danger: true }),
      );

      const placement = focusPage.placements.find((p: Placement) => p.id === selectedId);
      if (placement) {
        const current = previewPatch ? { ...placement, ...previewPatch } : placement;
        const rotateLabel = document.createElement("label");
        rotateLabel.className = "rotate-control";
        rotateLabel.title = "Rotation";
        const rotate = document.createElement("input");
        rotate.type = "range";
        rotate.min = "-180";
        rotate.max = "180";
        rotate.step = "1";
        rotate.dataset.action = "rotate";
        rotate.setAttribute("aria-label", "Rotation");
        rotate.value = String(Math.round((current.rotation * 180) / Math.PI));
        rotateLabel.appendChild(rotate);
        selectionTools.appendChild(rotateLabel);

        rotate.addEventListener("input", () => {
          previewPatch = { rotation: (Number(rotate.value) * Math.PI) / 180 };
          const photoEl = root.querySelector(
            `[data-placement-id="${selectedId}"]`,
          ) as HTMLElement | null;
          if (photoEl) applyPlacementStyle(photoEl, { ...placement, ...previewPatch });
        });
        rotate.addEventListener("change", () => {
          if (!selectedId || !focusPage) return;
          const snapshot = structuredClone(fresh);
          const patch = previewPatch ?? { rotation: (Number(rotate.value) * Math.PI) / 180 };
          previewPatch = null;
          void store
            .commitPlacementPreview(albumId, focusPage.id, selectedId, patch, snapshot)
            .then(() => paint());
        });
      }
      tools.appendChild(selectionTools);
    }

    view.append(chrome, spread, tools, pageNav);
    root.replaceChildren(view);

    function bindPagePointer(canvas: HTMLElement, pageIdx: number): void {
      const page = paintAlbum.pages[pageIdx];
      if (!page) return;

      canvas.addEventListener("pointerdown", (e) => {
        if (activePointer) return;
        currentPage = pageIdx;
        const rect = canvas.getBoundingClientRect();
        const point = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        const hit = [...page.placements].reverse().find((p) =>
          pointInPlacement(point, p, rect.width, rect.height),
        );
        if (!hit) {
          if (selectedId) {
            selectedId = null;
            previewPatch = null;
            void paint();
          }
          return;
        }
        const photoEl = canvas.querySelector(
          `[data-placement-id="${hit.id}"]`,
        ) as HTMLElement | null;
        if (!photoEl) return;

        if (selectedId !== hit.id) {
          selectedId = hit.id;
          previewPatch = null;
          void paint();
          return;
        }

        const target = e.target as HTMLElement;
        const handle = (target.dataset.handle as Handle | undefined) ?? "move";
        activePointer = {
          placementId: hit.id,
          pageId: page.id,
          pageIndex: pageIdx,
          handle,
          startX: e.clientX,
          startY: e.clientY,
          snapshot: { ...hit },
          albumSnapshot: structuredClone(paintAlbum),
          pageEl: canvas,
          photoEl,
        };
        previewPatch = null;
        canvas.setPointerCapture(e.pointerId);
      });

      canvas.addEventListener("pointermove", (e) => {
        if (!activePointer || activePointer.pageIndex !== pageIdx) return;
        const rect = canvas.getBoundingClientRect();
        const dx = (e.clientX - activePointer.startX) / rect.width;
        const dy = (e.clientY - activePointer.startY) / rect.height;
        const snap = e.shiftKey;
        const base = activePointer.snapshot;
        if (activePointer.handle === "move") {
          previewPatch = {
            x: snap ? snapValue(base.x + dx) : base.x + dx,
            y: snap ? snapValue(base.y + dy) : base.y + dy,
          };
        } else if (activePointer.handle === "resize-se") {
          previewPatch = {
            width: snap ? snapValue(base.width + dx) : base.width + dx,
            height: snap ? snapValue(base.height + dy) : base.height + dy,
          };
        } else if (activePointer.handle === "rotate") {
          const centerX = (base.x + base.width / 2) * rect.width;
          const centerY = (base.y + base.height / 2) * rect.height;
          const startAngle = Math.atan2(
            activePointer.startY - rect.top - centerY,
            activePointer.startX - rect.left - centerX,
          );
          const currentAngle = Math.atan2(
            e.clientY - rect.top - centerY,
            e.clientX - rect.left - centerX,
          );
          previewPatch = { rotation: base.rotation + (currentAngle - startAngle) };
        }
        applyPlacementStyle(activePointer.photoEl, { ...base, ...previewPatch });
      });

      const commit = () => {
        if (!activePointer || activePointer.pageIndex !== pageIdx) return;
        if (!previewPatch) {
          activePointer = null;
          return;
        }
        const { placementId, pageId, albumSnapshot } = activePointer;
        const patch = previewPatch;
        activePointer = null;
        previewPatch = null;
        void store
          .commitPlacementPreview(albumId, pageId, placementId, patch, albumSnapshot)
          .then(() => paint());
      };

      canvas.addEventListener("pointerup", commit);
      canvas.addEventListener("lostpointercapture", () => {
        if (activePointer && activePointer.pageIndex === pageIdx && previewPatch) commit();
        else if (activePointer && activePointer.pageIndex === pageIdx) activePointer = null;
      });
    }

    bindPagePointer(leftCanvas, leftIndex);
    if (fresh.pages[rightIndex]) bindPagePointer(rightCanvas, rightIndex);

    // If we rebuilt while a selection just became active, selection tools are present.
    // Re-bind photoEl after paint when selection changed mid-gesture is avoided by skipping paint on drag.

    backBtn.addEventListener("click", () => router.navigate({ name: "shelf" }));
    actions.querySelector('[data-action="view"]')?.addEventListener("click", () =>
      router.navigate({ name: "viewer", albumId, page: currentPage }),
    );
    actions.querySelector('[data-action="undo"]')?.addEventListener("click", () =>
      void store.undo(albumId).then(() => paint()),
    );
    actions.querySelector('[data-action="redo"]')?.addEventListener("click", () =>
      void store.redo(albumId).then(() => paint()),
    );
    actions.querySelector('[data-action="delete-album"]')?.addEventListener("click", () => {
      if (window.confirm(`Delete album "${fresh.title}"? This cannot be undone.`)) {
        void store.deleteAlbumById(albumId).then(() => router.navigate({ name: "shelf" }));
      }
    });

    titleInput.addEventListener("change", () =>
      void store.rename(albumId, titleInput.value).then(() => paint()),
    );

    tools.querySelector('[data-action="add-page"]')?.addEventListener("click", () =>
      void store.addPage(albumId).then(() => paint()),
    );
    tools.querySelector('[data-action="remove-page"]')?.addEventListener("click", () => {
      const p = fresh.pages[focusPageIndex];
      if (p && window.confirm("Remove this page?")) {
        void store.removePage(albumId, p.id).then(() => {
          currentPage = Math.max(0, Math.min(currentPage, fresh.pages.length - 2));
          selectedId = null;
          void paint();
        });
      }
    });
    tools.querySelector('[data-action="move-up"]')?.addEventListener("click", () => {
      if (!selectedId || !focusPage) return;
      void store.changeLayer(albumId, focusPage.id, selectedId, 1).then(() => paint());
    });
    tools.querySelector('[data-action="move-down"]')?.addEventListener("click", () => {
      if (!selectedId || !focusPage) return;
      void store.changeLayer(albumId, focusPage.id, selectedId, -1).then(() => paint());
    });

    importInput.addEventListener("change", async (e) => {
      const input = e.target as HTMLInputElement;
      const file = input.files?.[0];
      const latestAlbum = store.albums.find((candidate) => candidate.id === albumId) ?? fresh;
      const page = latestAlbum.pages[targetPageIndex(latestAlbum)];
      if (!file || !page) return;
      importError = null;
      try {
        const asset = await importPhotoFile(file);
        await store.addPlacement(albumId, page.id, asset.id);
      } catch (error) {
        importError = error instanceof Error ? error.message : "Import failed";
      }
      input.value = "";
      void paint();
    });

    tools.querySelector('[data-action="set-cover"]')?.addEventListener("click", async () => {
      if (!selectedId || !focusPage) return;
      const placement = focusPage.placements.find((p: Placement) => p.id === selectedId);
      if (placement) await store.setCover(albumId, placement.assetId);
    });

    tools.querySelector('[data-action="delete-photo"]')?.addEventListener("click", async () => {
      if (!selectedId || !focusPage) return;
      await store.removePlacement(albumId, focusPage.id, selectedId);
      selectedId = null;
      void paint();
    });

    if (!pendingImportHandled) {
      const pendingFile = takePendingImportFile();
      const page = fresh.pages[focusPageIndex];
      if (pendingFile && page) {
        pendingImportHandled = true;
        importError = null;
        try {
          const asset = await importPhotoFile(pendingFile);
          await store.addPlacement(albumId, page.id, asset.id);
        } catch (error) {
          importError = error instanceof Error ? error.message : "Import failed";
        }
        void paint();
      }
    }
  }

  const onKey = (e: KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    const fresh = store.albums.find((a) => a.id === albumId);
    if (!fresh) return;
    const pageIdx = targetPageIndex(fresh);
    const page = fresh.pages[pageIdx];
    if (!page) return;
    if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
      e.preventDefault();
      void store.removePlacement(albumId, page.id, selectedId).then(() => {
        selectedId = null;
        void paint();
      });
    }
    if (e.key === "ArrowLeft" && selectedId) {
      const placement = page.placements.find((p) => p.id === selectedId);
      if (placement) {
        e.preventDefault();
        const snapshot = structuredClone(fresh);
        void store
          .commitPlacementPreview(albumId, page.id, selectedId, { x: placement.x - 0.02 }, snapshot)
          .then(() => paint());
      }
    }
    if (e.key === "ArrowRight" && selectedId) {
      const placement = page.placements.find((p) => p.id === selectedId);
      if (placement) {
        e.preventDefault();
        const snapshot = structuredClone(fresh);
        void store
          .commitPlacementPreview(albumId, page.id, selectedId, { x: placement.x + 0.02 }, snapshot)
          .then(() => paint());
      }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === "z") {
      e.preventDefault();
      void store.undo(albumId).then(() => paint());
    }
    if ((e.metaKey || e.ctrlKey) && e.key === "y") {
      e.preventDefault();
      void store.redo(albumId).then(() => paint());
    }
  };

  window.addEventListener("keydown", onKey);
  const unsub = store.subscribe(() => {
    if (activePointer) return;
    void paint();
  });
  await paint();

  return () => {
    unsub();
    window.removeEventListener("keydown", onKey);
  };
}

function applyPreview(
  album: AlbumDocument,
  pageId: string,
  placementId: string,
  patch: Partial<Placement>,
): AlbumDocument {
  return {
    ...album,
    pages: album.pages.map((page) => {
      if (page.id !== pageId) return page;
      return {
        ...page,
        placements: page.placements.map((p) =>
          p.id === placementId ? { ...p, ...patch } : p,
        ),
      };
    }),
  };
}
